const React = require('react');
const { createElement: h, useState, useEffect } = React;
require('./RunInputModal.css');

// Centered styled popup (NOT a native popup) that collects the Start node's
// input parameters from the user before a manual run, then starts the run with
// the entered values. Reuses the shared .cdialog-* shell + a friendly on-brand
// .rim-* skin. Mirrors the sidebar editor: multiline params get a textarea,
// booleans a Yes/No select, numbers a number field, etc.
//
//   params:        [{ key, label, type, default, multiline }]
//   initialValues: { key: value }
//   onRun(values): start the run with the entered values (keyed by param key)
//   onCancel():    dismiss without running

function prettify(key) {
  var s = String(key || '');
  if (!s) return '';
  s = s.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ');
  return s.replace(/\b\w/g, function(c) { return c.toUpperCase(); });
}

function labelFor(p) {
  return (p && p.label) ? p.label : prettify(p && p.key);
}

function isMultiline(p) {
  return !!(p && (p.multiline === true || String(p.type || '').toLowerCase() === 'multiline'));
}

// A short, friendly type hint shown as a chip beside the label. Plain text
// fields return '' (no chip) so only the informative types are annotated.
function hintFor(p) {
  var t = String((p && p.type) || '').toLowerCase();
  if (t === 'number') return 'Number';
  if (t === 'boolean') return 'Yes / No';
  if (t === 'datetime') return 'Date & time';
  if (isMultiline(p)) return 'Long text';
  return '';
}

function inputTypeFor(p) {
  var t = String((p && p.type) || '').toLowerCase();
  if (t === 'number') return 'number';
  if (t === 'datetime') return 'datetime-local';
  return 'text';
}

function RunInputModal(props) {
  var params = Array.isArray(props.params) ? props.params : [];
  var onRun = props.onRun || function() {};
  var onCancel = props.onCancel || function() {};

  // Prefill from current values (falling back to each param's default) so the
  // user can just confirm, or tweak, the inputs.
  var [values, setValues] = useState(function() {
    var init = {};
    var src = props.initialValues || {};
    params.forEach(function(p) {
      if (!p || !p.key) return;
      var v = src[p.key] !== undefined ? src[p.key] : (p.default !== undefined ? p.default : '');
      init[p.key] = (v === null || v === undefined) ? '' : String(v);
    });
    return init;
  });

  useEffect(function() {
    function onKeyDown(e) { if (e.key === 'Escape') onCancel(); }
    document.addEventListener('keydown', onKeyDown);
    return function() { document.removeEventListener('keydown', onKeyDown); };
  }, []);

  function setOne(key, val) {
    setValues(function(prev) {
      var next = Object.assign({}, prev);
      next[key] = val;
      return next;
    });
  }
  function submit() { onRun(values); }

  // Pick the right control per param type, mirroring the sidebar editor.
  function renderControl(p, i) {
    var key = p.key;
    var val = values[key] !== undefined ? values[key] : '';
    var t = String((p && p.type) || '').toLowerCase();

    if (isMultiline(p)) {
      return h('textarea', {
        className: 'cdialog-field-input rim-input',
        rows: 4,
        value: val,
        placeholder: 'Enter ' + labelFor(p),
        autoFocus: i === 0,
        onChange: function(e) { setOne(key, e.target.value); }
      });
    }
    if (t === 'boolean') {
      return h('select', {
        className: 'cdialog-field-input rim-input',
        value: String(val || ''),
        onChange: function(e) { setOne(key, e.target.value); }
      },
        h('option', { value: '' }, 'Select…'),
        h('option', { value: 'true' }, 'Yes'),
        h('option', { value: 'false' }, 'No')
      );
    }
    // Single-line input: Enter submits (textareas keep Enter for newlines).
    return h('input', {
      className: 'cdialog-field-input rim-input',
      type: inputTypeFor(p),
      value: val,
      placeholder: 'Enter ' + labelFor(p),
      autoFocus: i === 0,
      onChange: function(e) { setOne(key, e.target.value); },
      onKeyDown: function(e) { if (e.key === 'Enter') submit(); }
    });
  }

  return h('div', { className: 'cdialog-overlay rim-overlay', onMouseDown: onCancel },
    h('div', {
      className: 'cdialog-box rim-box',
      onMouseDown: function(e) { e.stopPropagation(); }
    },
      h('div', { className: 'rim-icon' },
        h('svg', { width: 24, height: 24, viewBox: '0 0 24 24', fill: 'currentColor' },
          h('polygon', { points: '6 4 20 12 6 20 6 4' })
        )
      ),
      h('div', { className: 'cdialog-title' }, 'Run workflow'),
      h('div', { className: 'cdialog-message' }, 'Enter the inputs for this run.'),
      h('div', { className: 'cdialog-fields rim-fields' },
        params.map(function(p, i) {
          var hint = hintFor(p);
          return h('label', { key: p.key, className: 'cdialog-field rim-field' },
            h('span', { className: 'cdialog-field-label rim-field-label' },
              labelFor(p),
              hint ? h('span', { className: 'rim-hint' }, hint) : null
            ),
            renderControl(p, i)
          );
        })
      ),
      h('div', { className: 'cdialog-actions' },
        h('button', { className: 'cdialog-btn cdialog-btn-cancel', onClick: onCancel }, 'Cancel'),
        h('button', { className: 'cdialog-btn rim-btn-run', onClick: submit }, 'Run')
      )
    )
  );
}

module.exports = RunInputModal;
